import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useEffect, useLayoutEffect, useState } from "react";
import { openModal } from "../../../features/modalSLice";
import {
  getProjectWithMails,
  unsetSelectedProject,
} from "../../../features/projectsSlice";
import { getDonor, unsetSelectedDonor } from "../../../features/donorsSlice";
import Loading from "../../../components/loading/Loading";
import MainContainer from "../../../components/container/MainContainer";
import Modal from "../../../components/modal/Modal";
import PageTitle from "../../../components/titles/PageTitle";
import { ChevronLeft, Mail, MailPlus } from "lucide-react";
import PDFComponent from "../../../components/pdf/PDFComponent";
import useCustomBackButton from "../../../hooks/useCustomBackButton";
import SendFollowUpMail from "./SendFollowUpMail";
import useReformulatedData from "../../../hooks/useReformulatedData";
import ProposalReformulated from "../../../components/projects/ProposalReformulated";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";
import SaveEmailRecord from "../SaveEmailRecord";

const DonorProposalFollowUp = () => {
  const { donor_id, project_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthorized } = useAuthorization();
  const project = useSelector((state) => state.projects.selected); // List of ongoing projects
  const donor = useSelector((state) => state.donors.selected);
  const reformulations = useSelector((state) => state.donors.reformulations);
  const reformulationItems = useSelector(
    (state) => state.config.reformulationItems
  );

  const modalContent = useSelector((state) => state.modal.content);
  const donorLoad = useSelector(
    (state) => state.donors.load.getProjectWithMails
  );
  const projectLoad = useSelector((state) => state.projects.load.getProject);
  const [activeTab, setActiveTab] = useState("general");
  const [file, setFile] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const tabs = [
    { key: "general", label: "Project Info" },
    { key: "mails", label: "Emails" },
  ];

  //Get Blob from Document
  const getBlobFile = async (func) => {
    if (!file) {
      setFile(await func());
    }
  };

  const openSendMailModal = () => {
    dispatch(
      openModal(
        <SendFollowUpMail
          project_id={project.id}
          file={file}
          donor_id={donor_id}
          name={project.name}
        />
      )
    );
  };

  const openSaveRecordModal = () => {
    dispatch(
      openModal(
        <SaveEmailRecord
          project_id={project.id}
          donor_id={donor_id}
          name={project.name}
          donor={donor.name}
          type="follow up"
        />
      )
    );
  };

  useLayoutEffect(() => {
    dispatch(getProjectWithMails({ project_id, donor_id }));
    dispatch(getDonor(donor_id));
    return () => {
      dispatch(unsetSelectedDonor());
      // dispatch(unsetSelectedProject());
    };
  }, []);

  useEffect(() => {}, [donor]);

  useLayoutEffect(() => {
    if (
      (project.id && project?.stage?.step_number < 13) ||
      (project.id && project?.stage?.step_number > 13)
    ) {
      return navigate("/projects/reformulations", { replace: true });
    }
  }, [project, navigate]);

  const data = useReformulatedData({
    project,
    donor,
    donorLoad,
    reformulationItems,
    reformulations,
  });

  if (donorLoad === loadStatus.loading || projectLoad === loadStatus.loading) {
    return <Loading />;
  } else if (
    donorLoad === loadStatus.error ||
    projectLoad === loadStatus.error
  ) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <Modal>{modalContent}</Modal>
      {donor.id && project.id && (
        <div className="w-full max-w-5xl mx-auto text-text-1/70">
          <div className="sm:flex justify-between items-center py-4">
            <PageTitle title={donor.name + " - Follow Up"} />
          </div>
          {/* Navigation Bar */}
          <div className="border-b border-bkg2 bg-content">
            <div className="sm:hidden relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm border-primary text-text-1"
              >
                {tabs.find((tab) => tab.key === activeTab)?.label}
              </button>
              {showDropdown && (
                <div className="absolute z-10 bg-content border rounded shadow-lg w-full">
                  {tabs.map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => {
                        setActiveTab(tab.key);
                        setShowDropdown(false);
                      }}
                      className={`block w-full text-left py-2 px-4 text-sm ${
                        activeTab === tab.key
                          ? "bg-primary text-text-1/75"
                          : "text-text-1/75 hover:bg-bkg2"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <nav className="hidden sm:flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm text-text-1/80 ${
                    activeTab === tab.key
                      ? "border-primary text-primary"
                      : "border-transparent text-text-1 hover:text-text-1/50 hover:border-text-1"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          {/* Navigation End */}

          {project.id && (
            <div className="mx-auto max-w-5xl w-full bg-content shadow-md px-5 py-6 flex flex-col gap-0.5 ">
              {activeTab === "general" && (
                <>
                  {/* //Reformulated Display */}
                  <ProposalReformulated
                    project={project}
                    reformulationItems={reformulationItems}
                    reformulations={reformulations}
                  />
                </>
              )}
              {activeTab === "mails" && (
                <>
                  <div className="w-full text-text-1/70 bg-bkg border border-bkg2 rounded-md p-3 my-3 sm:flex justify-between items-center">
                    <div className="flex sm:mb-0 mb-5">
                      <Link
                        className="cancel-rss-btn"
                        to={"/reformulations/emails/follow-up/" + project_id}
                      >
                        <ChevronLeft size={18} />
                        Back
                      </Link>
                    </div>

                    <div className="flex gap-2 items-center justify-end ">
                      <button
                        type="button"
                        onClick={openSendMailModal}
                        className="add-rss-btn"
                        disabled={!isAuthorized("reformulation", "update")}
                      >
                        <Mail size={18} />
                        Mail
                      </button>
                      <button
                        type="button"
                        onClick={openSaveRecordModal}
                        className="add-rss-btn"
                        disabled={!isAuthorized("reformulation", "update")}
                      >
                        <MailPlus size={18} />
                        Record
                      </button>
                      <PDFComponent
                        title={project.name + ", Reformulated Proposal"}
                        data={data}
                        getFile={getBlobFile}
                      />
                    </div>
                  </div>
                  <div className="table-container !w-full  overflow-x-auto">
                    <>
                      {project.emails.length > 0 && (
                        <div className="mt-6 w-full ">
                          <h5 className="mb-2 font-medium  text-text-1/90 ">
                            Emails
                          </h5>
                          <div className="w-full ">
                            <table className="w-full">
                              <thead>
                                <tr className="bg-bkg text-nowrap">
                                  <td className="p-2 py-2 bg-gray-600 text-text-gen px-2">
                                    No
                                  </td>
                                  <td className="p-2 py-2 bg-gray-600 text-text-gen ">
                                    Subject
                                  </td>
                                  <td className="p-2 py-2 bg-gray-600 text-text-gen ">
                                    Date
                                  </td>
                                  <td className="p-2 py-2 bg-gray-600 text-text-gen text-center">
                                    Type
                                  </td>
                                  <td className="p-2 py-2 bg-gray-600 text-text-gen text-center">
                                    Platform
                                  </td>
                                </tr>
                              </thead>
                              <tbody>
                                {project.emails.map((item, index) => (
                                  <tr key={index}>
                                    <td className="p-2 px-2">{++index}</td>
                                    <td className="p-2 text-nowrap">
                                      {item.subject}
                                    </td>
                                    <td className="p-2 text-nowrap">
                                      {new Date(item.date).toDateString() ||
                                        "N/A"}
                                    </td>
                                    <td className="p-2 text-center">
                                      {item.type}
                                    </td>
                                    <td className="p-2 text-center">
                                      {item.platform}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  </div>
                </>
              )}
            </div>
          )}
          {/* Action Buttons  */}

          <div className="max-w-5xl w-full mx-auto">
            <div className="flex justify-between items-center pt-4 pb-10">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/reformulations/emails/follow-up/" + project_id);
                }}
                className="cancel-rss-btn"
              >
                <ChevronLeft size={18} />
                Back
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={openSendMailModal}
                  className="add-rss-btn"
                  disabled={!isAuthorized("reformulation", "update")}
                >
                  <Mail size={18} />
                  Mail
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainContainer>
  );
};

export default DonorProposalFollowUp;
